import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import { RootState } from '../../store';
import { IUserSate } from '../../store/user/reducers';
import { IWishlistState } from '../../store/wishlist/reducers';
import { syncWishlistIfRemoteChanged } from '../../store/wishlist/actions';
import {
    WISHLIST_INITIAL_DELAY_MS,
    WISHLIST_POLL_INTERVAL_MS,
    WISHLIST_VISIBILITY_DEBOUNCE_MS,
} from '../../config/backgroundSyncConfig';
import { isWishlistPollRoute } from '../../utils/wishlistPollRoutes';

interface StateProps {
    userState: IUserSate;
    wishlist: IWishlistState;
}

interface DispatchProps {
    syncWishlistIfRemoteChanged: () => Promise<void>;
}

type Props = StateProps & DispatchProps;

class WishlistSync extends Component<Props> {
    pollTimer: number | null = null;
    pollInFlight = false;
    lastVisibilityRefreshAt = 0;

    componentDidMount() {
        if (this.isAuthenticated()) {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            this.startPolling();
            window.setTimeout(this.pollWishlist, WISHLIST_INITIAL_DELAY_MS);
        }
    }

    componentDidUpdate(prevProps: Props) {
        const wasAuthenticated = !!prevProps.userState.user;
        const isAuthenticated = this.isAuthenticated();

        if (!wasAuthenticated && isAuthenticated) {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            this.startPolling();
            this.pollWishlist();
            return;
        }

        if (wasAuthenticated && !isAuthenticated) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            this.stopPolling();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.stopPolling();
    }

    isAuthenticated = () => !!this.props.userState.user;

    shouldPollOnCurrentRoute = () => isWishlistPollRoute(window.location.pathname);

    startPolling = () => {
        if (this.pollTimer !== null) {
            return;
        }

        this.pollTimer = window.setInterval(this.pollWishlist, WISHLIST_POLL_INTERVAL_MS);
    };

    stopPolling = () => {
        if (this.pollTimer !== null) {
            window.clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    };

    handleVisibilityChange = () => {
        if (document.hidden || !this.isAuthenticated()) {
            return;
        }

        const now = Date.now();
        if (now - this.lastVisibilityRefreshAt < WISHLIST_VISIBILITY_DEBOUNCE_MS) {
            return;
        }

        this.lastVisibilityRefreshAt = now;
        this.pollWishlist();
    };

    pollWishlist = async () => {
        if (document.hidden || this.pollInFlight || !this.isAuthenticated()) {
            return;
        }

        if (!this.shouldPollOnCurrentRoute()) {
            return;
        }

        if (this.props.wishlist.loading || this.props.wishlist.mutatingKey) {
            return;
        }

        this.pollInFlight = true;

        try {
            await this.props.syncWishlistIfRemoteChanged();
        } finally {
            this.pollInFlight = false;
        }
    };

    render() {
        return null;
    }
}

const mapStateToProps = (state: RootState): StateProps => ({
    userState: state.user.user,
    wishlist: state.wishlist,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, any>): DispatchProps => ({
    syncWishlistIfRemoteChanged: async () => {
        await dispatch(syncWishlistIfRemoteChanged());
    },
});

export default connect(mapStateToProps, mapDispatchToProps)(WishlistSync);
